/**
 * HOTmaths homepage copy.
 *
 * Carried across verbatim from `src/main/webapp/home/hotmaths/index.jsp`. The conversion changes order,
 * hierarchy and spacing — not words.
 *
 * Copy lives in the bundle rather than behind a JSON endpoint because it is editorial rather than
 * behavioural: paragraphs of marketing prose, reviewable in a diff, versioned with the page that renders
 * it. The brand *flags* that decide which sections exist come from `/web/ui/home/public/config` instead.
 */
import {HOME_URL} from '../routes';

export const hotmathsContent = {
    productName: 'Cambridge HOTmaths',

    /**
     * The page's opening statement.
     *
     * Deliberately NOT new copy. The page being replaced has no headline at all - the login form occupies
     * the whole top section - so this uses the product name and the opening clause of the existing About
     * paragraph (index.jsp:35), and the button labels already on the page.
     *
     * A real marketing headline is a copywriting decision, not a developer's. Every competitor reviewed
     * leads with a proposition rather than a product name; that is worth having written, but it needs to
     * come from whoever owns the messaging.
     */
    intro: {
        heading: 'Cambridge HOTmaths',
        standfirst: 'An interactive online maths learning, teaching and assessment resource for students '
            + 'and teachers, for individuals or whole classes, for school and at home.',
        image: '/home/template/v2/img/banners/hotmath.jpg',
        primaryAction: {label: 'Get a 14-day trial', href: '#ch-home-trial'},
        secondaryAction: {label: 'View sample lessons', href: '#ch-home-lessons'},
    },

    /** From index.jsp:207-213, unchanged. Icons are presentational only. */
    features: {
        heading: 'What you get',
        items: [
            {label: 'Lessons', icon: 'lessons'},
            {label: 'Interactive and printable resources', icon: 'resources'},
            {label: 'Walkthroughs', icon: 'walkthrough'},
            {label: 'Questions', icon: 'questions'},
            {label: 'Scorcher', icon: 'scorcher'},
            {label: 'Dictionary', icon: 'dictionary'},
        ],
    },

    /** From index.jsp:101-117, unchanged. */
    trial: {
        sectionHeading: 'Try Cambridge HOTmaths',
        heading: 'Get a 14-day trial',
        body: 'Explore teaching and learning tools including lessons, walkthroughs, questions, animated '
            + 'demonstrations, worksheets and solutions, working mathematically investigations, the maths '
            + 'dictionary and our online maths competition.',
        teacherNote: 'If you are a teacher, we can extend your free trial for a school term, or organise '
            + 'a whole-class trial so that you can explore the full benefits of HOTmaths.',
    },

    /** From index.jsp:215-236. Only the school price is copy - the rest comes from the product table. */
    pricing: {
        heading: 'Pricing',
        schoolPrice: '$28.00 AUD (inc GST)',
        schoolNote: 'per student per school year',
        priceUnavailable: 'Prices are temporarily unavailable. Please contact us.',
        contactEmail: 'enquiries@cambridge.edu.au',
    },

    video: {
        heading: 'See it in action',
        poster: '/home/template/v2/img/banners/video.jpg',
        /*
         * Same files the Struts page serves (index.jsp:78-80), minus the HLS variant.
         *
         * mp4 and webm play natively in every current browser; the .m3u8 needs video.js or an HLS
         * library, which is a dependency for a single marketing video on a page whose whole point is to
         * carry less. All three URLs were checked and return 206.
         *
         * Note the original markup has `src=https://...webm"` with the opening quote missing, so the webm
         * source has never actually been offered on that page.
         */
        sources: [
            {src: 'https://cdn.edjin.com/home/hotmaths/videos/hotmaths-video.mp4', type: 'video/mp4'},
            {src: 'https://cdn.edjin.com/home/hotmaths/videos/hotmaths-video.webm', type: 'video/webm'},
        ],
        actionLabel: 'View our video gallery',
        galleryHref: 'https://www.youtube.com/watch?v=V8caUg1BhEk',
        webinarNote: 'Or watch our online Professional Development webinars on YouTube',
    },

    about: {
        heading: 'About Cambridge HOTmaths',
        paragraphs: [
            'Cambridge HOTmaths is a comprehensive mathematics learning system – an interactive online '
                + 'maths learning, teaching and assessment resource for students and teachers, for '
                + 'individuals or whole classes, for school and at home.',
            'A suite of flexible resources (printable and online) is provided for all types of classrooms '
                + 'ranging from those with no technology to full laptop access, and includes projectable '
                + 'material in all lessons for interactive and non-interactive whiteboards.',
            'Developed by a dedicated group of educators, technicians and designers, Cambridge HOTmaths is '
                + 'a high-quality maths resource for students and teachers.',
            'Since joining forces with Cambridge University Press in 2009, a range of products have been '
                + 'published with integrated digital resources powered by Cambridge HOTmaths, including '
                + 'Essential Mathematics, Cambridge Senior Mathematics, ICE-EM Mathematics Third Edition '
                + 'and CambridgeMATHS Stage 6. These products deliver a host of interactive features to '
                + 'enhance the teaching and learning experience, and when connected to a class teacher '
                + 'account offer a powerful Learning Management System.',
        ],
        links: [
            {label: 'Read more about us', modal: 'aboutModal'},
            {label: 'About the writers', modal: 'writersModal'},
        ],
    },

    otherProducts: {
        heading: 'Other Cambridge products',
        body: 'Are you looking for Essential Mathematics Second Edition or Cambridge Senior Mathematics?',
        link: {label: 'cambridge.edu.au/hotmaths', href: 'https://cambridge.edu.au/hotmaths'},
    },

    /**
     * The four public sample lessons. These link straight into real content with no account, which is the
     * page's strongest asset and something no competitor reviewed offers — see
     * claude/competitor-homepage-analysis.md.
     */
    sampleLessons: {
        heading: 'Sample lessons',
        intro: 'Access widgets, walkthroughs, questions and Scorcher in each sample lesson listed below.',
        note: 'Some functionality is available only to subscribers.',
        lessons: [
            {level: 'Middle primary', topic: 'Multiplication & division', title: 'Modelling array & groups', syllabusId: 166},
            {level: 'Upper primary', topic: 'Volume & capacity', title: 'Linking litres & cubes', syllabusId: 106, includesGames: true},
            {level: 'Early secondary', topic: 'Representing data', title: 'Creating divided bar graphs', syllabusId: 146},
            {level: 'Middle secondary', topic: 'Trigonometry: introduction', title: 'Applying trigonometry', syllabusId: 186},
        ],
    },

    /*
     * "Home" was href="#", which scrolls to the top and leaves a bare "#" in the address bar rather than
     * going anywhere; it now points at the page properly, and SiteShell marks it as the current one.
     *
     * The trial link is "Try HOTmaths" rather than the section's full heading, "Try Cambridge HOTmaths".
     * The longer form took 216px of a 385px phone screen, which forced the three items onto three lines
     * and made the header a slab of dark before any content. The logo above already says Cambridge.
     */
    nav: [
        {label: 'Home', href: HOME_URL},
        {label: 'Try HOTmaths', href: '#ch-home-trial'},
        {label: 'Help', href: '/usersCommon/showHelp.action'},
    ],
};

export default hotmathsContent;
